import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map } from 'lodash';
import Vue from 'vue';

import CouponApi from '@/extensions/shop/store/api/coupon';
import CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';
import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

const defaultForm = {};
const defaultRules = {};
const defaultUi = {};

export const useShopCouponStore = defineStore('shopCoupon', {
    state: () => ({
        loading: true,
        couponsPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100],
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            link: {},
            defaultSort: null,
            items: [],
        },
        options: {
            applicableFor: {
                loading: false,
                items: [
                    { value: 'anyone', label: 'Anyone' },
                    { value: 'customer-group', label: 'Customer Groups' },
                    { value: 'customer', label: 'Customers' },
                    { value: 'new-customer', label: 'New Customers' },
                    { value: 'guest', label: 'Guests' },
                ],
            },
            customerGroup: {
                loading: false,
                items: [],
            },
        },
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi),
    }),

    getters: {
        formInitialized: (state) => get(state, 'ui.form.initialized'),
        formValidateOnEnter: (state) => get(state, 'ui.form.validateOnEnter'),
    },

    actions: {
        addPaginatedCouponsToState(data) {
            this.couponsPaginated.items = data.items;
            this.couponsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.couponsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.couponsPaginated.link = cloneDeep(data.config.link);
            this.couponsPaginated.defaultSort = get(data.config, 'defaultSort') || null;
            this.couponsPaginated.showFilters = get(data.config, 'showFilters') || false;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.couponsPaginated.allColumns, (column) => {
                let allowed = find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.couponsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.couponsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.couponsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.couponsPaginated.filters, key);
            } else {
                Vue.set(this.couponsPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            Vue.set(this.couponsPaginated, 'showFilters', value);
        },

        defineSorting({ key, value }) {
            Vue.set(this.couponsPaginated.variables, 'order', key);
            Vue.set(this.couponsPaginated.variables, 'direction', value);
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRule({ key, rules }) {
            Vue.set(this.rules, key, rules);
        },

        resetRules() {
            Vue.set(this, 'rules', cloneDeep(defaultRules));
        },

        updateCustomerGroupOptionsLoading(value) {
            Vue.set(this.options.customerGroup, 'loading', value);
        },

        insertCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.customerGroup, 'items', customerGroups);
        },

        resetUI() {
            Vue.set(this, 'ui', cloneDeep(defaultUi));
        },

        insertCoupon(coupon) {
            Vue.set(this, 'form', coupon);
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        setFormStatus(value) {
            Vue.set(this.form, 'status', value);
        },

        // Async actions
        fetchCouponsPaginated() {
            return CouponApi.fetchCouponsPaginated(this.couponsPaginated).then((data) => {
                this.addPaginatedCouponsToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchCoupon(id) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;

            return CouponApi.fetchCoupon({ id, cms: cms.id }).then((coupon) => {
                this.insertCoupon(cloneDeep(coupon));
            });
        },

        create(coupon) {
            coupon = cloneDeep(coupon);

            return CouponApi.create(coupon).then((coupon) => {
                this.insertCoupon(cloneDeep(coupon));
                this.resetRules();
                return coupon;
            });
        },

        update(coupon) {
            return CouponApi.update(coupon).then((coupon) => {
                this.insertCoupon(cloneDeep(coupon));
                this.resetRules();
                return coupon;
            });
        },

        delete(id) {
            return CouponApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        reset() {
            this.insertCoupon(cloneDeep(defaultForm));
        },

        fetchCustomerGroupOptions(shopID) {
            this.updateCustomerGroupOptionsLoading(true);
            return CustomerGroupApi.fetchList(shopID)
                .then((customerGroups) => {
                    this.updateCustomerGroupOptionsLoading(false);
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch((err) => {
                    this.updateCustomerGroupOptionsLoading(false);
                    throw err;
                });
        },
    }
});
